/**
 * Layout principal de la aplicación autenticada.
 *
 * Compone la barra lateral (`Sidebar`) y una cabecera con el botón de menú, el
 * selector de sede (multi-sede), el nombre del usuario y el cierre de sesión,
 * alrededor del `<Outlet />` de React Router. Se usa como ruta envolvente, dentro
 * de `RequireAuth`, para todas las pantallas de dominio:
 *
 *     <Route element={<RequireAuth />}>
 *       <Route element={<AppLayout />}>
 *         <Route path="/" element={<Dashboard />} />
 *         …
 *       </Route>
 *     </Route>
 *
 * La barra lateral se oculta/muestra por completo con el botón de la cabecera en
 * todos los dispositivos (estado en `useSidebar`): en escritorio (≥lg) forma
 * parte del flujo y empuja el contenido; en móvil/tableta (<lg) es un drawer en
 * overlay con backdrop, que cierra al pulsar fuera, con Esc o al navegar.
 *
 * Presentacional/estructural: la sesión y sus acciones vienen de `useAuth`; no
 * hay lógica de datos aquí.
 */
import { useEffect } from "react";
import { Outlet } from "react-router-dom";

import { SedeSelector } from "@/features/auth";
import { NotificationsBellGate } from "@/features/notifications";
import { Button } from "@/components/ui";
import { useAuth } from "@/lib/auth";

import { useSidebar } from "../useSidebar";
import { Sidebar } from "./Sidebar";

export function AppLayout() {
  const { user, logout } = useAuth();
  const { isOpen, toggle, close } = useSidebar();

  // En móvil/tableta el drawer se cierra con Escape; el listener solo vive
  // mientras está abierto. En escritorio Esc también cierra, lo cual es inocuo
  // (basta el botón para reabrir).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        close();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  return (
    <div className="flex min-h-screen bg-[#e2ecf9] bg-[linear-gradient(165deg,#d8e8f8_0%,#e6f0fc_38%,#dceaf8_100%)]">
      {/*
        Barra lateral. Se renderiza solo cuando está abierta para que cerrada no
        ocupe espacio ni quede en el árbol accesible (no navegable ni enfocable).
        En escritorio es parte del flujo (empuja el `<main>`); en móvil/tableta es
        un drawer fijo en overlay, con un backdrop que cierra al pulsar fuera.
      */}
      {isOpen && (
        <>
          <div
            // Backdrop solo en móvil/tableta; en escritorio la barra es parte del
            // flujo y no hace falta atenuar el fondo.
            className="fixed inset-0 z-30 bg-black/50 lg:hidden"
            aria-hidden="true"
            onClick={close}
          />
          <aside className="fixed inset-y-0 left-0 z-40 w-64 shrink-0 border-r border-[#7eb3e0] bg-[#d8e9f7] shadow-[0_0_0_1px_rgba(37,99,235,0.12),0_4px_28px_rgba(29,78,216,0.12)] lg:static lg:z-auto lg:shadow-none">
            {/* En móvil/tableta navegar cierra el drawer; en escritorio (lg) no. */}
            <Sidebar onNavigate={isDesktop() ? undefined : close} />
          </aside>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center justify-between gap-4 border-b border-[#bfd4ea] bg-white/80 px-4 backdrop-blur-sm">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggle}
            aria-label={isOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={isOpen}
            aria-controls="navegacion-principal"
          >
            {isOpen ? <CloseIcon /> : <MenuIcon />}
          </Button>

          <div className="flex items-center gap-4">
            <NotificationsBellGate />
            <SedeSelector />
            {user && (
              <span className="hidden text-sm text-slate-600 sm:inline">
                {user.name}
              </span>
            )}
            <Button variant="secondary" size="sm" onClick={() => void logout()}>
              Cerrar sesión
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** `true` si el viewport es de escritorio (≥lg); degrada a `true` sin matchMedia. */
function isDesktop(): boolean {
  return window.matchMedia?.("(min-width: 1024px)")?.matches ?? true;
}

/** Icono hamburguesa (menú cerrado). Trazo fino, sin relleno. */
function MenuIcon() {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

/** Icono X (menú abierto). Mismo lenguaje line-style que la hamburguesa. */
function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
